import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Chip } from "@mui/material";
import MainApi from "../../../../../../services/mainApi.service";
import { memo, useCallback, useMemo, useState } from "react";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { UnchosenProfessionalPriority } from "./UnchosenProfessionalPriorityForm/UnchosenProfessionalPriority";
import { ITicketAutRecRuleUnchosenProfessionalRules } from "../../../../../../models/ticket-aut-rec-rule-unchosen-professional-rule";

const TICKET_AUT_REC_RULE_UNCHOSEN_PROFESSIONAL_RULE_ROUTE =
  process.env.REACT_APP_AUTOMATIONS +
  "/api/v1/TicketAutRecRuleUnchosenProfessionalRule/";
const ticketAutomationReceivedRulePreferenceService =
  new MainApi<ITicketAutRecRuleUnchosenProfessionalRules>(
    TICKET_AUT_REC_RULE_UNCHOSEN_PROFESSIONAL_RULE_ROUTE,
  );

const translationPath =
  "chatBot.page.automations.automationReceivedRules.ticketAutRecRuleUnchosenProfessionalRule.";

const UnchosenProfessionalPriorityPage = () => {
  const { t } = useTranslation();

  const { id: ticketAutomationReceivedRuleId } = useParams();

  const [preferenceForm, setPreferenceForm] = useState(false);

  const [selectedId, setSelectedId] = useState<string>();

  const columns: GridColDef<ITicketAutRecRuleUnchosenProfessionalRules>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "priority",
      headerName: t(translationPath + "priority") || "Priority",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "unchosenProfessionalCriterion",
      headerName:
        t(translationPath + "unchosen_professional_criterion") ||
        "Unchosen Professional Criterion",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "ticketAutRecRuleUnchosenProfessionalRuleInsurances",
      headerName: t(translationPath + "insurances") || "Insurances",
      renderCell: ({
        row: { ticketAutRecRuleUnchosenProfessionalRuleInsurances },
      }) => {
        if (ticketAutRecRuleUnchosenProfessionalRuleInsurances.length === 0)
          return "-";

        const displayName = ticketAutRecRuleUnchosenProfessionalRuleInsurances
          .map((item) => item.externalName)
          .join("; ");

        return (
          <OverflowTooltip
            text={displayName}
            tooltip={displayName}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "calendars",
      headerName: t(translationPath + "calendars") || "Calendars",
      renderCell: ({
        row: { ticketAutRecRuleUnchosenProfessionalRuleOffers },
      }) => {
        if (ticketAutRecRuleUnchosenProfessionalRuleOffers.length === 0)
          return "-";

        const displayName =
          ticketAutRecRuleUnchosenProfessionalRuleOffers
            .filter((item) => item.calendarExternalName)
            .map((item) => item.calendarExternalName)
            .join("; ") || "-";

        return (
          <OverflowTooltip
            text={displayName}
            tooltip={displayName}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "physicians",
      headerName: t(translationPath + "physicians") || "Physicians",
      renderCell: ({
        row: { ticketAutRecRuleUnchosenProfessionalRuleOffers },
      }) => {
        if (ticketAutRecRuleUnchosenProfessionalRuleOffers.length === 0)
          return "-";

        const displayName =
          ticketAutRecRuleUnchosenProfessionalRuleOffers
            .filter((item) => item.doctorExternalName)
            .map((item) => item.doctorExternalName)
            .join("; ") || "-";

        return (
          <OverflowTooltip
            text={displayName}
            tooltip={displayName}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "ticketAutRecRuleUnchosenProfessionalRuleSpecialties",
      headerName: t(translationPath + "specialties") || "Specialties",
      renderCell: ({
        row: { ticketAutRecRuleUnchosenProfessionalRuleSpecialties },
      }) => {
        if (ticketAutRecRuleUnchosenProfessionalRuleSpecialties.length === 0)
          return "-";

        const displayName = ticketAutRecRuleUnchosenProfessionalRuleSpecialties
          .map((item) => item.externalName)
          .join("; ");

        return (
          <OverflowTooltip
            text={displayName}
            tooltip={displayName}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      renderCell: ({ row: { enable } }) => {
        return (
          <Chip
            label={
              enable
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={enable ? "success" : "error"}
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    setPreferenceForm(true);
  };

  const handleRowClick = (
    params: GridRowParams<ITicketAutRecRuleUnchosenProfessionalRules>,
  ) => {
    if (!params.row) return;
    setPreferenceForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = useCallback(() => {
    setSelectedId(undefined);
    setPreferenceForm(false);
  }, []);

  const props = useMemo(() => {
    return {
      TicketAutomationReceivedRuleId: ticketAutomationReceivedRuleId,
    };
  }, [ticketAutomationReceivedRuleId]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {preferenceForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <UnchosenProfessionalPriority
            id={selectedId}
            ticketAutomationReceivedRuleId={ticketAutomationReceivedRuleId}
            onBackButton={handleBack}
          />
        </Box>
      )}
      {!preferenceForm && (
        <Box height={600}>
          <Table<ITicketAutRecRuleUnchosenProfessionalRules>
            showDefaultMacroFilters={false}
            service={ticketAutomationReceivedRulePreferenceService}
            columns={columns}
            title={""}
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputAutoFocus={false}
            defaultPageSize={20}
            serviceProps={props}
            pageSizeOptions={[5, 10, 20]}
            searchInputPlaceHolder={""}
            addButtonLabel={
              t(translationPath + "add_unchosenProfessionalPriority") ||
              "Add Unchosen Professional Priority"
            }
            confirmOptions={{
              title: t(translationPath + "confirm.title"),
              description: t(translationPath + "confirm.description"),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(UnchosenProfessionalPriorityPage);
